/**
 * LiveAgentContext — carries the branding + agent-identity bundle that nearly
 * every live-agent surface needs.
 *
 * Before this, the same 8 props (brand colors, agent name/id/userId, logo,
 * capture callback) were threaded by hand through ~10 components, some taking
 * 30–40 props. Components inside `LiveAgentProvider` read them from here
 * instead; the page sets them once.
 */
import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";

export interface LiveAgentContextValue {
  /** Resolved primary brand color (agent branding, falling back to the default). */
  brandColor: string;
  secondaryColor?: string | null;
  agentName?: string | null;
  agentId?: string;
  agentUserId?: string;
  /** Square logo used on QR codes / avatars. */
  qrLogoUrl?: string | null;
  logoUrl?: string | null;
  /** Quietly tell the Genie the visitor just left an email, so it doesn't re-ask. */
  onContactCaptured: (email: string) => void;
}

const LiveAgentContext = createContext<LiveAgentContextValue | null>(null);

export const LiveAgentProvider = ({
  children,
  ...value
}: LiveAgentContextValue & { children: ReactNode }) => {
  const memoized = useMemo<LiveAgentContextValue>(
    () => value,
    // The page rebuilds these each render; only re-publish when one actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      value.brandColor,
      value.secondaryColor,
      value.agentName,
      value.agentId,
      value.agentUserId,
      value.qrLogoUrl,
      value.logoUrl,
      value.onContactCaptured,
    ],
  );
  return <LiveAgentContext.Provider value={memoized}>{children}</LiveAgentContext.Provider>;
};

/** Guarded accessor — throws outside `LiveAgentProvider`. */
export const useLiveAgent = (): LiveAgentContextValue => {
  const ctx = useContext(LiveAgentContext);
  if (!ctx) throw new Error("useLiveAgent must be used within a LiveAgentProvider");
  return ctx;
};

/**
 * Non-throwing accessor — returns null outside `LiveAgentProvider`. Components
 * that double as standalone (the studio renders them without a provider) use
 * this to read context as a fallback layer under their own props.
 */
export const useLiveAgentOptional = (): LiveAgentContextValue | null =>
  useContext(LiveAgentContext);
